import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { resolveRoute } from '@/plugin/slots'
import { FlaskConical, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DataBenchJobPanel } from '@/components/platform/data-bench-job-panel'
import { DataBenchRemoval } from '@/components/platform/platform-data-bench-removal'
import { PaneListSkeleton } from '@/components/layout/pane-list-skeleton'
import {
  useRunSimulationHireEvents,
  useSimulationCapabilities,
  useSimulationJob,
  useSimulationJobStream,
  useSimulationScenario,
  type SimulationScenario,
} from '@/hooks/use-platform-simulation'
import { dataBenchCopy } from '@/lib/platform-data-bench-copy'

export function DataBenchWorldPage({ scenarioId }: { scenarioId: string }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: capabilities } = useSimulationCapabilities()
  const { data: scenario, isLoading, error } = useSimulationScenario(scenarioId)
  const runHire = useRunSimulationHireEvents()
  const copy = dataBenchCopy(capabilities?.packs?.[0])
  const hireEventsEnabled = Boolean(capabilities?.hire_events_enabled) && copy.supportsHire

  const [applyCount, setApplyCount] = useState(3)
  const [inviteCount, setInviteCount] = useState(0)
  const [jobId, setJobId] = useState<string | undefined>()
  const hydratedJobId = jobId || scenario?.active_job?.id || undefined
  const jobQuery = useSimulationJob(hydratedJobId, Boolean(hydratedJobId))
  useSimulationJobStream(hydratedJobId, scenarioId)

  useEffect(() => {
    const status = jobQuery.data?.status
    if (status === 'completed') {
      toast.success('Applications recorded on this run')
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenario', scenarioId] })
      setJobId(undefined)
    } else if (status === 'failed') {
      toast.error(jobQuery.data?.error || 'Could not record applications')
      setJobId(undefined)
    }
  }, [jobQuery.data?.status, jobQuery.data?.error, qc, scenarioId])

  if (isLoading) {
    return (
      <PageContainer>
        <PaneListSkeleton ariaLabel="Loading product world" variant="row" />
      </PageContainer>
    )
  }
  if (error || !scenario) {
    return (
      <PageContainer>
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Run not found'}
        </p>
        <div className="mt-3">
          <Button type="button" variant="outline" onClick={() => navigate('/platform/simulation')}>
            Back
          </Button>
        </div>
      </PageContainer>
    )
  }

  const jobHref = scenario.opportunity_id
    ? resolveRoute('opportunity.manage', { id: scenario.opportunity_id })
    : null
  const orgJobsHref =
    (scenario.organisation_slug
      ? resolveRoute('organisation.opportunities', { slug: scenario.organisation_slug })
      : null) ?? resolveRoute('opportunities.manage')

  return (
    <PageContainer>
      <PageHeader
        leading={
          <PageHeaderLeading back={{ label: 'Back to Simulation', to: '/platform/simulation' }} />
        }
        title={scenario.name}
        description="People, organisations, jobs, and applications from this run stay in the tenant for rubric work. They stay off recruiter, employer, and candidate screens until you remove them here."
        icon={<FlaskConical className="h-5 w-5" />}
      />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>What this is</CardTitle>
            <CardDescription alwaysShow>
              This run wrote real tenant rows for rubric work. Those rows stay off recruiter,
              employer, and candidate screens. Real testers still sign themselves up.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              <span className="text-muted-foreground">Organisation · </span>
              {scenario.organisation_name || scenario.organisation_slug || '—'}
            </p>
            <p>
              <span className="text-muted-foreground">Sample job · </span>
              {scenario.opportunity_title || '—'}
            </p>
            <p>
              <span className="text-muted-foreground">People · </span>
              {scenario.profile_ids?.length ?? 0}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {jobHref ? (
                <Button type="button" asChild>
                  <Link to={jobHref}>Open job as employer</Link>
                </Button>
              ) : null}
              {orgJobsHref ? (
                <Button type="button" variant="outline" asChild>
                  <Link to={orgJobsHref}>Browse employer jobs</Link>
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <DataBenchRemoval scenarioId={scenarioId} />

        <HireEventsCard
          scenario={scenario}
          hireEventsEnabled={hireEventsEnabled}
          applyCount={applyCount}
          inviteCount={inviteCount}
          setApplyCount={setApplyCount}
          setInviteCount={setInviteCount}
          pending={runHire.isPending || Boolean(hydratedJobId && jobQuery.data?.status !== 'completed')}
          job={
            hydratedJobId
              ? {
                  status: jobQuery.data?.status || 'queued',
                  progress: jobQuery.data?.progress,
                  error: jobQuery.data?.error,
                }
              : null
          }
          onRun={() => {
            void runHire
              .mutateAsync({
                scenarioId,
                apply_count: applyCount,
                invite_count: inviteCount,
                async_job: true,
              })
              .then((job) => {
                if (job.id && job.id !== 'sync') setJobId(job.id)
                else toast.success('Applications recorded on this run')
              })
              .catch((err: unknown) =>
                toast.error(err instanceof Error ? err.message : 'Could not record applications'),
              )
          }}
        />
      </div>
    </PageContainer>
  )
}

export function HireEventsCard({
  scenario,
  hireEventsEnabled,
  applyCount,
  inviteCount,
  setApplyCount,
  setInviteCount,
  pending,
  job,
  onRun,
}: {
  scenario: SimulationScenario
  hireEventsEnabled: boolean
  applyCount: number
  inviteCount: number
  setApplyCount: (n: number) => void
  setInviteCount: (n: number) => void
  pending: boolean
  job: { status: string; progress?: { done?: number; total?: number }; error?: string | null } | null
  onRun: () => void
}) {
  const scored = scenario.status === 'scored'
  return (
    <Card>
      <CardHeader>
        <CardTitle>Record applications</CardTitle>
        <CardDescription alwaysShow>
          Scoring a sample does not create applications. Apply or invite people here to record
          them on this run. They stay hidden from employer Pipeline and Talent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!hireEventsEnabled ? (
          <p className="text-sm text-muted-foreground">
            Hire simulation is off on this API. A platform operator can turn it on.
          </p>
        ) : null}
        {!scored ? (
          <p className="text-sm text-muted-foreground">
            Score the pool first. Invites need match scores above the invite threshold.
          </p>
        ) : (
          <Badge variant="secondary">Pool is scored</Badge>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="apply-n">Applies</Label>
            <Input
              id="apply-n"
              type="number"
              className="w-24"
              min={0}
              max={50}
              value={applyCount}
              disabled={!hireEventsEnabled}
              onChange={(e) => setApplyCount(Number(e.target.value) || 0)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-n">Invites</Label>
            <Input
              id="invite-n"
              type="number"
              className="w-24"
              min={0}
              max={50}
              value={inviteCount}
              disabled={!hireEventsEnabled}
              onChange={(e) => setInviteCount(Number(e.target.value) || 0)}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={!hireEventsEnabled || pending}
            onClick={onRun}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Record applications
          </Button>
        </div>
        {job ? (
          <DataBenchJobPanel
            kind="hire_events"
            status={job.status}
            progress={job.progress}
            error={job.error}
          />
        ) : null}
      </CardContent>
    </Card>
  )
}
